import { useCallback, useEffect, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import {
  useLazyGetGoogleSigninUrlQuery,
  useLazyGoogleCallbackQuery,
  useSendOtpMutation,
  useVerifyOtpMutation,
} from '@/api/auth/authApi';
import { config } from '@/config/env';
import { useAppDispatch } from '@/store/hooks';
import { setUser } from '@/store/slices/authSlice';

import { getErrorMessage, useToast } from '@/components/atoms/Toast';

const OTP_RESEND_TIMEOUT = 30; // seconds before resend is allowed
const MAX_OTP_RESEND_COUNT = 3; // max number of OTPs that can be sent

function resolveGoogleSigninUrl(response: string | { url?: string } | undefined): string {
  if (typeof response === 'string') return response.trim();
  return response?.url?.trim() || '';
}

export function useLoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const toast = useToast();

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimeout, setOtpTimeout] = useState(0);
  const [otpResendCount, setOtpResendCount] = useState(MAX_OTP_RESEND_COUNT);
  const [contactOpen, setContactOpen] = useState(false);
  const [isGoogleSigningIn, setIsGoogleSigningIn] = useState(false);

  const [sendOtpApi, { isLoading: isSendingOtp }] = useSendOtpMutation();
  const [verifyOtpApi, { isLoading: isVerifyingOtp }] = useVerifyOtpMutation();
  const [getGoogleSigninUrl] = useLazyGetGoogleSigninUrlQuery();
  const [googleCallback] = useLazyGoogleCallbackQuery();

  const fromPath = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
  const returnTo = fromPath && fromPath !== '/login' ? fromPath : '/home';

  useEffect(() => {
    if (!otpTimeout) return;
    const interval = setInterval(() => setOtpTimeout(prev => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [otpTimeout]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    const error = searchParams.get('error');

    if (error) {
      toast.error('Google sign-in was cancelled or failed');
      queueMicrotask(() => {
        navigate('/login', { replace: true });
      });
      return;
    }

    if (!code) return;

    googleCallback({ code })
      .unwrap()
      .then(data => {
        if (!data?.token || !data?.id || !data?.emailId) {
          toast.error('Invalid Google login response');
          return;
        }

        localStorage.setItem('accessToken', data.token);
        localStorage.setItem('user', JSON.stringify(data));
        localStorage.setItem('userDetails', JSON.stringify(data));
        dispatch(setUser(data));
        toast.success('Signed in successfully');
        queueMicrotask(() => {
          navigate(returnTo, { replace: true });
        });
      })
      .catch(err => toast.error(getErrorMessage(err)));
  }, [dispatch, googleCallback, navigate, returnTo, toast]);

  const sendOtp = useCallback(async () => {
    if (!email.trim()) return;
    await sendOtpApi({ emailId: email })
      .unwrap()
      .then(() => {
        setOtpSent(true);
        setOtpTimeout(OTP_RESEND_TIMEOUT);
        setOtpResendCount(prev => prev - 1);
        toast.success('OTP sent to your email');
      })
      .catch(err => {
        console.log('err', err);
        toast.error(getErrorMessage(err));
      });
  }, [email, sendOtpApi, toast]);

  const resendOtp = useCallback(async () => {
    if (otpTimeout > 0 || otpResendCount <= 0) return;
    await sendOtpApi({ emailId: email })
      .unwrap()
      .then(() => {
        setOtpTimeout(OTP_RESEND_TIMEOUT);
        setOtpResendCount(prev => prev - 1);
        toast.success('OTP resent to your email');
      })
      .catch(err => toast.error(getErrorMessage(err)));
  }, [email, otpTimeout, otpResendCount, sendOtpApi, toast]);

  const verifyOtp = useCallback(async () => {
    if (!otp.trim()) return;
    await verifyOtpApi({ emailId: email, otp })
      .unwrap()
      .then(data => {
        if (!data?.token) {
          toast.error('Invalid login response');
          return;
        }
        localStorage.setItem('accessToken', data.token);
        localStorage.setItem('user', JSON.stringify(data));
        dispatch(setUser(data));
        toast.success('Logged in successfully');
        queueMicrotask(() => {
          navigate(returnTo, { replace: true });
        });
      })
      .catch(err => toast.error(getErrorMessage(err)));
  }, [email, otp, verifyOtpApi, navigate, dispatch, toast, returnTo]);

  const resetEmailState = useCallback(() => {
    setOtpSent(false);
    setOtpTimeout(0);
    setOtpResendCount(MAX_OTP_RESEND_COUNT);
    setOtp('');
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setIsGoogleSigningIn(true);

    await getGoogleSigninUrl()
      .unwrap()
      .then(data => {
        const signinUrl = resolveGoogleSigninUrl(data);
        const fallbackUrl = `${config.apiBaseUrl}/auth/v1/playground/googleSignin`;
        window.location.assign(signinUrl || fallbackUrl);
      })
      .catch(err => {
        setIsGoogleSigningIn(false);
        toast.error(getErrorMessage(err));
      });
  }, [getGoogleSigninUrl, toast]);

  const openContact = useCallback(() => setContactOpen(true), []);
  const closeContact = useCallback(() => setContactOpen(false), []);

  return {
    email,
    setEmail,
    otp,
    setOtp,
    otpSent,
    otpTimeout,
    otpResendCount,
    isSendingOtp,
    isVerifyingOtp,
    sendOtp,
    resendOtp,
    verifyOtp,
    resetEmailState,
    signInWithGoogle,
    contactOpen,
    openContact,
    closeContact,
    isGoogleSigningIn,
  };
}
